import { FC } from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { MissionAIGenerateFormData } from './mission-ai-generate.schema';

interface MissionAIGenerateFormProps {
  control: Control<MissionAIGenerateFormData>;
  errors: FieldErrors<MissionAIGenerateFormData>;
  isLoading: boolean;
}

const MissionAIGenerateForm: FC<MissionAIGenerateFormProps> = ({ control, errors, isLoading }) => {
  return (
    <>
      <div>
        <label htmlFor="business_activities" className="block text-sm font-medium text-gray-700 mb-2">
          Business Activities <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="business_activities"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="business_activities"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.business_activities ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Memproduksi perangkat lunak manajemen proyek berbasis AI dan menyediakan konsultasi implementasi"
              disabled={isLoading}
            />
          )}
        />
        {errors.business_activities && (
          <p className="text-sm text-red-500 mt-1">{errors.business_activities.message}</p>
        )}
        <p className="text-xs text-gray-500 mt-1">
          Deskripsi aktivitas utama perusahaan saat ini (Apa yang dilakukan?). Harus mencakup kegiatan inti yang
          menciptakan nilai. Maksimal 1 kalimat. Contoh: Memproduksi perangkat lunak manajemen proyek berbasis AI dan
          menyediakan konsultasi implementasi.
        </p>
      </div>

      <div>
        <label htmlFor="hr_focus" className="block text-sm font-medium text-gray-700 mb-2">
          HR Focus <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="hr_focus"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="hr_focus"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.hr_focus ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Memastikan retensi talenta melalui pengembangan karier yang terstruktur..."
              disabled={isLoading}
            />
          )}
        />
        {errors.hr_focus && <p className="text-sm text-red-500 mt-1">{errors.hr_focus.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Fokus utama divisi sumber daya manusia (Bagaimana tim dikembangkan?). Harus terkait dengan talenta, budaya,
          atau pengembangan SDM. Minimal 1 kalimat. Contoh: Memastikan retensi talenta melalui pengembangan karier yang
          terstruktur dan memupuk budaya kerja yang inovatif.
        </p>
      </div>

      <div>
        <label htmlFor="operational_focus" className="block text-sm font-medium text-gray-700 mb-2">
          Operational Focus <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="operational_focus"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="operational_focus"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.operational_focus ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Mencapai efisiensi proses 99% melalui otomasi alur kerja..."
              disabled={isLoading}
            />
          )}
        />
        {errors.operational_focus && <p className="text-sm text-red-500 mt-1">{errors.operational_focus.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Fokus utama divisi operasional (Bagaimana proses dijalankan?). Harus terkait dengan efisiensi, kualitas, atau
          standar operasional. Maksimal 1 kalimat. Contoh: Mencapai efisiensi proses 99% melalui otomasi alur kerja dan
          memelihara kualitas produk dengan standar ISO 9001.
        </p>
      </div>

      <div>
        <label htmlFor="marketing_focus" className="block text-sm font-medium text-gray-700 mb-2">
          Marketing Focus <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="marketing_focus"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="marketing_focus"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.marketing_focus ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Membangun kesadaran merek sebagai 'pemimpin pemikiran' di industri..."
              disabled={isLoading}
            />
          )}
        />
        {errors.marketing_focus && <p className="text-sm text-red-500 mt-1">{errors.marketing_focus.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Fokus utama divisi pemasaran (Bagaimana produk/layanan dipromosikan?). Harus terkait dengan branding, customer
          acquisition, atau engagement. Maksimal 1 kalimat. Contoh: Membangun kesadaran merek sebagai 'pemimpin
          pemikiran' di industri dan meningkatkan tingkat konversi leads sebesar 15%.
        </p>
      </div>

      <div>
        <label htmlFor="finance_focus" className="block text-sm font-medium text-gray-700 mb-2">
          Finance Focus <span className="text-gray-400">(Optional)</span>
        </label>
        <Controller
          name="finance_focus"
          control={control}
          render={({ field }) => (
            <textarea
              {...field}
              id="finance_focus"
              rows={3}
              className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                errors.finance_focus ? 'border-red-500' : 'border-gray-300'
              }`}
              placeholder="Mempertahankan margin laba bersih di atas 20%..."
              disabled={isLoading}
            />
          )}
        />
        {errors.finance_focus && <p className="text-sm text-red-500 mt-1">{errors.finance_focus.message}</p>}
        <p className="text-xs text-gray-500 mt-1">
          Fokus utama divisi keuangan (Bagaimana sumber daya dikelola?). Harus terkait dengan profitabilitas, investasi,
          atau manajemen risiko. Maksimal 1 kalimat. Contoh: Mempertahankan margin laba bersih di atas 20% dan melakukan
          investasi strategis pada R&D untuk pertumbuhan jangka panjang.
        </p>
      </div>
    </>
  );
};

export default MissionAIGenerateForm;
